import { z } from 'zod';
import { MessageStatus, MessageType } from '../enums';
import { ConversationMetadataSchema } from '../schemas/metadata.schemas';

/**
 * Conversation id validator that accepts a UUID OR the legacy
 * `conv-<digits>[-<agentSlug>]` shape.
 *
 * Why both? The agent invocation flow used to mint conversation ids as
 * `conv-${Date.now()}-${agentId}` and persist them as the row's primary
 * key. The generator now uses `randomUUID()`, but rows minted before the
 * switch still live in local / staging / prod databases and have to
 * stay openable, deletable, and writable from the chat UI. A bare
 * `z.string().uuid()` validator on those routes 400-outs every legacy
 * row at the tRPC input layer before the handler can run.
 *
 * Use this on any procedure whose `id` / `conversationId` input may
 * reach a pre-existing conversation row (byId, delete, saveMessage, etc.).
 * Do NOT use it as the SHAPE of a newly-minted id — generators should
 * always emit UUIDs going forward; this is a back-compat reader only.
 */
export const ConversationIdSchema = z
  .string()
  .regex(
    /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|conv-\d+(-[A-Za-z0-9_-]+)?)$/,
    'Expected UUID or legacy conv-<timestamp>[-<agentSlug>] id',
  );

/**
 * Message DTO Schema
 *
 * Extended schema for memory service integration:
 * - embedding: Vector representation for semantic search (1536 dimensions)
 * - importance: Relevance score from 0-1 (for conversation prioritization)
 * - summary: Compressed version of message for context reduction
 * - parentMessageId: Links to previous related message for threading
 * - tags: Semantic labels for categorization and filtering
 */
export const MessageSchema = z.object({
  id: z.string().uuid(),
  conversationId: z.string().uuid(),
  type: z.nativeEnum(MessageType),
  role: z.enum(['user', 'assistant', 'system']),
  content: z.string().max(32000),
  status: z.nativeEnum(MessageStatus).default(MessageStatus.COMPLETED),
  metadata: ConversationMetadataSchema.optional(),
  tokenCount: z.number().int().nonnegative().optional(),
  embedding: z.array(z.number()).length(1536).optional(),
  importance: z.number().min(0).max(1).optional(),
  summary: z.string().max(1000).optional(),
  parentMessageId: z.string().uuid().optional(),
  tags: z.array(z.string()).optional(),
  createdAt: z.date(),
});

export type Message = z.infer<typeof MessageSchema>;

/**
 * Create Message DTO Schema (for API requests)
 * conversationId is optional as it can be extracted from the route parameter
 * Memory fields (embedding, importance, summary, tags) are optional and managed by memory service
 */
export const CreateMessageSchema = z.object({
  conversationId: z.string().uuid().optional(),
  type: z.nativeEnum(MessageType),
  role: z.enum(['user', 'assistant', 'system']),
  content: z.string().min(1).max(32000),
  metadata: ConversationMetadataSchema.optional(),
  parentMessageId: z.string().uuid().optional(),
});

export type CreateMessage = z.infer<typeof CreateMessageSchema>;

/**
 * Update Message DTO Schema (for editing messages)
 * Allows updating content and memory-related fields
 */
export const UpdateMessageSchema = z.object({
  content: z.string().min(1).max(32000).optional(),
  metadata: ConversationMetadataSchema.optional(),
  importance: z.number().min(0).max(1).optional(),
  summary: z.string().max(1000).optional(),
  tags: z.array(z.string()).optional(),
});

export type UpdateMessage = z.infer<typeof UpdateMessageSchema>;

/**
 * Conversation DTO Schema
 */
export const ConversationSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  agentId: z.string().uuid(),
  userId: z.string().uuid(),
  title: z.string().max(255).nullable().optional(),
  metadata: ConversationMetadataSchema.optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  lastMessageAt: z.date().nullable().optional(),
});

export type Conversation = z.infer<typeof ConversationSchema>;

/**
 * Create Conversation DTO Schema (for API requests)
 * businessId and userId are optional in the request body as they are extracted from JWT
 */
export const CreateConversationSchema = z.object({
  businessId: z.string().uuid().optional(),
  agentId: z.string().uuid(),
  userId: z.string().uuid().optional(),
  title: z.string().max(255).optional(),
  metadata: ConversationMetadataSchema.optional(),
});

export type CreateConversation = z.infer<typeof CreateConversationSchema>;

/**
 * Update Conversation DTO Schema
 */
export const UpdateConversationSchema = z.object({
  title: z.string().max(255).optional(),
  metadata: ConversationMetadataSchema.optional(),
});

export type UpdateConversation = z.infer<typeof UpdateConversationSchema>;

/**
 * Conversation with Messages DTO Schema
 */
export const ConversationWithMessagesSchema = ConversationSchema.extend({
  messages: z.array(MessageSchema),
});

export type ConversationWithMessages = z.infer<typeof ConversationWithMessagesSchema>;
